import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  PartialMatchRouteSnapshot,
  Route,
  Router,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { TenantService } from '../services/tenant.service';
import { PermissionService } from '../services/permission.service';
import { TenantInfo } from '../models/tenant.model';
import { FeatureKey, PermissionLevel } from '../constants/feature-keys';

/** Route data that declares the permission a route requires. */
export interface PermissionRouteData {
  feature?: FeatureKey;
  level?: PermissionLevel; // defaults to 'read'
  requireAdmin?: boolean; // admin-only routes (e.g. user management)
}

function check(
  data: PermissionRouteData | undefined,
  tenantId: string | null,
): Observable<boolean | UrlTree> {
  const tenantService = inject(TenantService);
  const permissions = inject(PermissionService);
  const router = inject(Router);

  return tenantService.loadTenants().pipe(
    take(1),
    map((tenants) => {
      const tenant =
        tenants.find((t) => t.tenantId === tenantId) ?? tenants[0];
      return allowed(tenant, data, permissions)
        ? true
        : fallbackTree(tenant?.tenantId, router);
    }),
    catchError(() => of(router.createUrlTree(['/home']))),
  );
}

function allowed(
  tenant: TenantInfo | undefined,
  data: PermissionRouteData | undefined,
  permissions: PermissionService,
): boolean {
  if (!data) {
    return true; // no requirement declared → allow (UX guard only)
  }
  if (data.requireAdmin) {
    return !!tenant?.isAdmin;
  }
  if (data.feature) {
    return permissions.can(tenant, data.feature, data.level ?? 'read');
  }
  return true;
}

function fallbackTree(tenantId: string | undefined, router: Router): UrlTree {
  return tenantId
    ? router.createUrlTree(['/', tenantId, 'home'])
    : router.createUrlTree(['/home']);
}

/**
 * Generic route guard enforcing group-based permissions (docs §8.1). Replaces AdminGuard.
 * Wire on EVERY feature route via both canActivate (authoritative — uses the :tenantId route param)
 * and canMatch (best-effort — prevents lazy-loading a forbidden feature module when the active
 * tenant is already known). The required permission comes from route data:
 *   data: { feature: FEATURE.banners, level: 'write' }   // or { requireAdmin: true }
 * Client-side guard is UX only; the backend enforces the real 403.
 */
export const permissionGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
): Observable<boolean | UrlTree> => {
  const tenantService = inject(TenantService);
  const tenantId =
    route.paramMap.get('tenantId') ??
    route.parent?.paramMap.get('tenantId') ??
    tenantService.getActiveTenantId();
  return check(route.data as PermissionRouteData, tenantId);
};

// canMatch runs during URL matching, before tenantGuard's canActivate has set the active tenant.
// For child routes the :tenantId is not in `segments`, so on a cold direct-link the active id
// may be unknown — defer to canActivate (which has the route param) by allowing the match.
export const permissionCanMatch: CanMatchFn = (
  _route: Route,
  _segments: UrlSegment[],
  _currentSnapshot?: PartialMatchRouteSnapshot,
): Observable<boolean | UrlTree> => {
  const tenantService = inject(TenantService);
  const tenantId = tenantService.getActiveTenantId();
  if (!tenantId) {
    return of(true);
  }
  return check(_route.data as PermissionRouteData, tenantId);
};
